import { useTelefoneInterno, type ServidorDeRede, type Telefone as Aparelho } from '@mymaxx/ui';
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { useEffect } from 'react';
import { api } from '../lib/api';
import { useDiscar } from '../lib/discar';
import { useSocketInterno } from '../lib/interno-tempo-real';
import { pararToque, tocarToque } from '../lib/som';

/**
 * A chamada de voz dentro do painel.
 *
 * O áudio vai direto de um navegador ao outro; o servidor só apresenta os dois lados. Nada
 * é gravado — fica o registro de quem ligou, quando e por quanto tempo, que aparece como
 * uma linha na própria conversa.
 *
 * Mora dentro do provedor da conversa interna, e não na tela de Conversas: quem recebe uma
 * ligação pode estar no monitor, nos relatórios ou atendendo cliente, e precisa ouvir o
 * telefone do mesmo jeito. É o mesmo motivo que mantém o socket vivo no painel inteiro.
 */

/** O aparelho: liga o motor da chamada ao socket e desenha a janelinha. */
export function Telefone() {
  const socket = useSocketInterno();
  const chamada = useTelefoneInterno(socket, buscarServidores);

  const definir = useDiscar((e) => e.definir);
  useEffect(() => {
    definir(chamada.podeLigar ? chamada.ligarPara : null);
    return () => definir(null);
    // `ligarPara` é estável o bastante: o que muda de verdade é haver socket ou não.
  }, [chamada.podeLigar, definir]);

  // O telefone só soa para quem está recebendo: quem ligou já sabe que ligou.
  useEffect(() => {
    if (chamada.estado === 'tocando') tocarToque();
    else pararToque();
    return () => pararToque();
  }, [chamada.estado]);

  return <JanelaDaChamada chamada={chamada} />;
}

/** Os servidores de rede que o navegador deve tentar, pedidos à API na hora de ligar. */
async function buscarServidores(): Promise<ServidorDeRede[]> {
  const { servidores } = await api.get<{ servidores: ServidorDeRede[]; toqueMaximoMs: number }>(
    '/v1/interno/chamadas/credenciais',
  );
  return servidores;
}

/**
 * A janelinha da chamada: tocando, chamando ou falando.
 *
 * Flutua acima de tudo e segue a pessoa pelas telas — a ligação não pertence à tela de
 * Conversas, pertence a quem está falando.
 */
export function JanelaDaChamada({ chamada }: { chamada: Aparelho }) {
  if (chamada.estado === 'parada') return null;

  const tocando = chamada.estado === 'tocando';
  const falando = chamada.estado === 'falando';

  return (
    <div className="fixed right-4 bottom-4 z-50 w-72 rounded-xl border border-neutral-200 bg-white p-4 shadow-xl dark:border-neutral-700 dark:bg-neutral-900">
      <div className="flex items-center gap-3">
        <span
          className={`flex size-12 shrink-0 items-center justify-center rounded-full bg-marca-100 text-marca-700 dark:bg-marca-900 dark:text-marca-100 ${
            tocando ? 'animate-pulse' : ''
          }`}
        >
          <Phone size={20} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{chamada.comQuem?.nome ?? 'Chamada'}</p>
          <p className="text-xs text-neutral-500">
            {tocando
              ? 'está ligando para você'
              : chamada.estado === 'chamando'
                ? 'chamando…'
                : chamada.estado === 'conectando'
                  ? 'conectando…'
                  : relogio(chamada.duracao)}
          </p>
        </div>
      </div>

      {chamada.erro && <p className="mt-2 text-xs text-red-600">{chamada.erro}</p>}

      <div className="mt-3 flex justify-center gap-2">
        {tocando && (
          <button
            type="button"
            onClick={() => void chamada.atender()}
            className="flex items-center gap-1 rounded-full bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
          >
            <Phone size={16} /> Atender
          </button>
        )}
        {falando && (
          <button
            type="button"
            title={chamada.mudo ? 'Ligar o microfone' : 'Desligar o microfone'}
            aria-label={chamada.mudo ? 'Ligar o microfone' : 'Desligar o microfone'}
            onClick={chamada.alternarMudo}
            className={`rounded-full p-2 ${
              chamada.mudo
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
                : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
            }`}
          >
            {chamada.mudo ? <MicOff size={16} /> : <Mic size={16} />}
          </button>
        )}
        <button
          type="button"
          onClick={() => (tocando ? chamada.recusar() : chamada.desligar())}
          className="flex items-center gap-1 rounded-full bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700"
        >
          <PhoneOff size={16} /> {tocando ? 'Recusar' : 'Desligar'}
        </button>
      </div>
    </div>
  );
}

/** "0:42", "12:03" — o relógio da conversa. */
function relogio(segundos: number): string {
  const m = Math.floor(segundos / 60);
  const s = segundos % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
